/**
 * loanTypesAdmin.service.ts — Admin > Payments > Loan Types (legacy
 * admin_loan_types_list.php): the account's loan templates, which fill in a
 * new loan's terms on the Loans page, through /loan-types. Writes need Admin
 * Access.
 *
 * As legacy, the Due Day is kept in the type's `intervalAmount` (loans keep it
 * there too), the interval is monthly only and the interest simple only.
 */
import { apiClient } from '@/services/apiClient';
import type { ApiResponse } from '@/types/api.types';

export interface AdminLoanType {
  id: number;
  title: string;
  /** 'monthly'. */
  loanInterval: string;
  /** Day of the month a payment is due, 1–28 (legacy's `intervalAmount`). */
  dueDay: number;
  /** Loan Term, in months. */
  loanMonths: number;
  lateFee: number;
  /** Percent. */
  interestRate: number;
  /** 'simple'. */
  interestType: string;
  active: boolean;
}

export type LoanTypePayload = Omit<AdminLoanType, 'id' | 'active'>;

/** Legacy's choices. */
export const LOAN_INTERVAL_OPTIONS = [{ label: 'Monthly', value: 'monthly' }];
export const INTEREST_TYPE_OPTIONS = [{ label: 'Simple', value: 'simple' }];
export const DUE_DAY_OPTIONS = Array.from({ length: 28 }, (_, i) => ({ label: String(i + 1), value: i + 1 }));

export const LOAN_TYPE_TITLE_MAX = 255;
export const LOAN_MONTHS_MAX = 600;
export const LATE_FEE_MAX = 99999.99;

function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

export function toLoanType(raw: Record<string, unknown>): AdminLoanType {
  return {
    id: num(raw.loanTypeID),
    title: String(raw.title ?? ''),
    loanInterval: String(raw.loanInterval ?? 'monthly').toLowerCase() || 'monthly',
    dueDay: num(raw.intervalAmount),
    loanMonths: num(raw.loanMonths),
    lateFee: num(raw.lateFee),
    interestRate: num(raw.interestRate),
    interestType: String(raw.interestType ?? 'simple').toLowerCase() || 'simple',
    active: raw.active === true || raw.active === 1 || raw.active === '1',
  };
}

/** Every type, deleted ones too. */
export function listLoanTypes(): Promise<AdminLoanType[]> {
  return apiClient
    .get<ApiResponse<Record<string, unknown>[]>>('/loan-types')
    .then((r) => (r.data.data ?? []).map(toLoanType));
}

function body(data: LoanTypePayload) {
  return {
    title: data.title.trim(),
    loanInterval: data.loanInterval,
    intervalAmount: data.dueDay,
    loanMonths: data.loanMonths,
    lateFee: data.lateFee,
    interestRate: data.interestRate,
    interestType: data.interestType,
  };
}

export function createLoanType(data: LoanTypePayload): Promise<void> {
  return apiClient.post('/loan-types', body(data)).then(() => undefined);
}

export function updateLoanType(id: number, data: LoanTypePayload): Promise<void> {
  return apiClient.put(`/loan-types/${id}`, body(data)).then(() => undefined);
}

export function deleteLoanType(id: number): Promise<void> {
  return apiClient.delete(`/loan-types/${id}`).then(() => undefined);
}

export function restoreLoanType(id: number): Promise<void> {
  return apiClient.post(`/loan-types/${id}/restore`).then(() => undefined);
}
